/*
 * Name tags: parts of a material's ServiceM8 name that the app adds, rather than people typing them.
 * Each business sets up its own (Price List settings), of three kinds:
 *   - choices: one of a few, in brackets on the end of the first line, e.g. Installation:
 *       " (Supply Only)", " (Labour Only)", " (12 Month Warranty)";
 *   - includes: things an item includes, excludes or doesn't mention, each set on its own and
 *     listed together: " (Includes Keys & Programming, Excludes Batteries)". A choice option worded
 *     "Includes ..." or "Excludes ..." joins that list: " (Includes Installation & Keys)";
 *   - lines: a last line with a value, e.g. "SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)".
 * Several together: " (Supply Only, 12 Month Warranty, Includes Keys, Excludes Batteries)".
 * The tags live in the name itself (ServiceM8 is the source of truth), so typing them in ServiceM8
 * turns them on here too. Old wording (aliases, and retired tags) is still read, so names written
 * before a tag was reworded or deleted read correctly until they're updated.
 * Shared by the server and the screens; no imports.
 */

/** One option of a choice: its wording in the name (also what people pick). */
export interface TagOption {
    id: string;
    text: string;
    /** Earlier wordings, still read out of names. */
    aliases?: string[];
}

/** One of a few options (or none), e.g. Installation: Includes Installation / Supply Only / Labour Only. */
export interface ChoiceTag {
    id: string;
    label: string;
    options: TagOption[];
}

/** Something an item can include or exclude, set on its own: "Includes Keys", "Excludes Keys". */
export interface IncludeTag {
    id: string;
    /** As it reads after "Includes" or "Excludes", e.g. "Keys". */
    name: string;
    aliases?: string[];
}

/** A line under the name with a value, e.g. "SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)". */
export interface LineTag {
    id: string;
    label: string;
    /** The line's text, before the value in brackets. */
    text: string;
    /** The values to pick from (an item can also have one that isn't listed). */
    values: string[];
    aliases?: string[];
}

export interface NameTagConfig {
    choices: ChoiceTag[];
    includes: IncludeTag[];
    lines: LineTag[];
    /**
     * Wording of deleted tags: still read out of names (so updating a name takes them off), never
     * written. `includes` holds words of either list.
     */
    retired: { segments: string[]; includes: string[]; lines: string[] };
}

/** The two lists in the brackets, in the order they're written: what an item includes, and what it doesn't. */
export const LIST_KINDS = ["includes", "excludes"] as const;
export type ListKind = (typeof LIST_KINDS)[number];
const LIST_WORDS: Record<ListKind, string> = { includes: "Includes", excludes: "Excludes" };

export interface NameParts {
    /** The name people type: everything but the tags. */
    base: string;
    /** Choice id -> the option picked. Not there: none. */
    choices: Record<string, string>;
    /** Ids of the include tags it includes, and of those it excludes (never both). */
    includes: string[];
    excludes: string[];
    /** Line tag id -> its value ("" = none given). Not there: no line. */
    lines: Record<string, string>;
}

/** One change to a name's parts. Each control sends only what it changed, applied to the latest saved name. */
export interface NameChange {
    /** The typed name. */
    base?: string;
    choice?: { id: string; option: string | null };
    /** An include tag: included, excluded, or not mentioned (null). */
    include?: { id: string; state: ListKind | null };
    line?: { id: string; value: string | null };
}

/** "Includes Installation" -> includes "Installation": an option worded like that joins that list. */
function listItem(text: string): { kind: ListKind; item: string } | null {
    const t = text.trim();
    for (const kind of LIST_KINDS) {
        // "Includes" or "Include", "Excludes" or "Exclude".
        const item = after(t, `${kind} `) ?? after(t, `${kind.slice(0, -1)} `);
        if (item) return { kind, item };
    }
    return null;
}

export const newId = () => crypto.randomUUID().slice(0, 8);

/** "A", "A & B", "A, B & C". */
function joinAnd(list: readonly string[]) {
    if (list.length < 2) return list.join("");
    return `${list.slice(0, -1).join(", ")} & ${list.at(-1)}`;
}

/** Text after `prefix` (any case), trimmed, or null if it doesn't start with it. */
function after(text: string, prefix: string): string | null {
    return text.toLowerCase().startsWith(prefix.toLowerCase()) ? text.slice(prefix.length).trim() : null;
}

/** The same words, whatever their case or the spaces around them. */
export const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
const wordings = (x: { aliases?: string[] }, main: string) => [main, ...(x.aliases ?? [])];

/** Splits an "Installation, Keys & Programming" style list ("&", "and" or commas). */
const listWords = (list: string) =>
    list
        .replaceAll("&", ",")
        .split(",")
        .flatMap((part) => part.replaceAll(/\s+/g, " ").split(/ and /i))
        .map((w) => w.trim())
        .filter(Boolean);

/** What goes in the brackets: "Supply Only, Includes Keys, Excludes Batteries", or "". */
export function tagText(p: Pick<NameParts, "choices" | ListKind>, cfg: NameTagConfig) {
    const segments: string[] = [];
    const lists: Record<ListKind, string[]> = { includes: [], excludes: [] };
    for (const c of cfg.choices) {
        const o = c.options.find((x) => x.id === p.choices[c.id]);
        if (!o) continue;
        const listed = listItem(o.text);
        if (listed) lists[listed.kind].push(listed.item);
        else segments.push(o.text.trim());
    }
    for (const kind of LIST_KINDS) {
        for (const i of cfg.includes) if (p[kind].includes(i.id)) lists[kind].push(i.name.trim());
        if (lists[kind].length) segments.push(`${LIST_WORDS[kind]} ${joinAnd(lists[kind])}`);
    }
    return segments.join(", ");
}

/** A line tag as written: "SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)" (the value in capitals when the text is). */
export function lineText(tag: Pick<LineTag, "text">, value: string) {
    const text = tag.text.trim();
    if (!value.trim()) return text;
    return `${text} (${text === text.toUpperCase() ? value.trim().toUpperCase() : value.trim()})`;
}

type Parsed = Pick<NameParts, "choices" | ListKind>;

/** The choice option worded "`kind` `word`" (e.g. "Includes Installation"), if there is one. */
const listedOption = (cfg: NameTagConfig, kind: ListKind, word: string) =>
    cfg.choices
        .flatMap((c) => c.options.map((o) => ({ c, o })))
        .find(({ o }) =>
            wordings(o, o.text).some((t) => {
                const listed = listItem(t);
                return listed?.kind === kind && same(listed.item, word);
            }),
        );

/** Reads one word of a `kind` list into `out`; false if it isn't a tag, or the other list has it. */
function readWord(w: string, kind: ListKind, cfg: NameTagConfig, out: Parsed): boolean {
    const inc = cfg.includes.find((i) => wordings(i, i.name).some((t) => same(t, w)));
    if (inc) {
        if (out[kind === "includes" ? "excludes" : "includes"].includes(inc.id)) return false;
        if (!out[kind].includes(inc.id)) out[kind].push(inc.id);
        return true;
    }
    const choice = listedOption(cfg, kind, w);
    if (choice) {
        // Two of a one-only choice's options (an item tagged both before that was checked): still its tags, the first kept, so
        // updating the name replaces them rather than leaving them in its typed part.
        out.choices[choice.c.id] ??= choice.o.id;
        return true;
    }
    return cfg.retired.includes.some((t) => same(t, w));
}

/** Reads an "Installation & Keys" style list of `kind` into `out`; false if any of it isn't a tag. Any order and case. */
function readList(list: string, kind: ListKind, cfg: NameTagConfig, out: Parsed): boolean {
    const words = listWords(list);
    return words.length > 0 && words.every((w) => readWord(w, kind, cfg, out));
}

/** The comma before the next list in a list's text: ", Excludes ..." (list words can't hold commas). */
const NEXT_LIST = /,\s*(?=(?:includes?|excludes?)\s)/i;

/** Reads the lists that end a bracket ("Includes A & B, Excludes C") into `out`; false if it's anything but lists of tags. */
function readLists(text: string, cfg: NameTagConfig, out: Parsed): boolean {
    const read = new Set<ListKind>();
    let rest = text;
    while (rest) {
        const list = listItem(rest);
        if (!list || read.has(list.kind)) return false;
        read.add(list.kind);
        const next = list.item.search(NEXT_LIST);
        if (!readList(next === -1 ? list.item : list.item.slice(0, next), list.kind, cfg, out)) return false;
        rest = next === -1 ? "" : list.item.slice(next).replace(/^,\s*/, "");
    }
    return true;
}

/** The bracket's tags, or null if it's anything else (then the whole ending is left as part of the typed name). */
function parseTags(inside: string, cfg: NameTagConfig): Parsed | null {
    const out: Parsed = { choices: {}, includes: [], excludes: [] };
    // Segments other than the lists: choice options not worded "Includes ..." or "Excludes ...", longest first.
    const segments = [
        ...cfg.choices.flatMap((c) =>
            c.options.flatMap((o) => wordings(o, o.text).map((text) => ({ text: text.trim(), choice: c.id as string | null, option: o.id as string | null }))),
        ),
        ...cfg.retired.segments.map((text) => ({ text: text.trim(), choice: null, option: null })),
    ]
        .filter((s) => s.text && !listItem(s.text))
        .sort((a, b) => b.text.length - a.text.length);
    let rest = inside.trim();
    if (!rest) return null;
    while (rest) {
        if (listItem(rest)) return readLists(rest, cfg, out) ? out : null;
        const seg = segments.find((s) => {
            const tail = after(rest, s.text);
            return tail !== null && (tail === "" || tail.startsWith(","));
        });
        if (!seg) return null;
        if (seg.choice && seg.option) {
            if (out.choices[seg.choice]) return null;
            out.choices[seg.choice] = seg.option;
        }
        rest = rest.slice(seg.text.length).trim().replace(/^,\s*/, "");
    }
    return out;
}

/** A first line's ending "(...)" (with nothing after it): where it starts and what's inside, or null. */
function bracketsAtEnd(line: string): { start: number; inside: string } | null {
    const trimmed = line.trimEnd();
    if (!trimmed.endsWith(")")) return null;
    const start = trimmed.lastIndexOf("(");
    if (start === -1) return null;
    const inside = trimmed.slice(start + 1, -1);
    return inside.includes(")") ? null : { start, inside };
}

/** A line's value for a line tag ("" when it gives none), or null when the line isn't that tag. */
function lineValue(line: string, texts: string[]): string | null {
    for (const t of texts.toSorted((a, b) => b.length - a.length)) {
        const rest = after(line.trim(), t.trim());
        if (rest === null) continue;
        if (!rest) return "";
        if (rest.startsWith("(") && rest.endsWith(")")) return rest.slice(1, -1).trim();
    }
    return null;
}

/** Split a ServiceM8 name into the typed name and its tags. */
export function splitName(full: string, cfg: NameTagConfig): NameParts {
    const lines = full.split(/\r?\n/);
    const found: Record<string, string> = {};
    // Each line tag: the last line that is one (normally the last lines). Never the first line. The longest first, so a line's
    // whole wording ("SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)") wins over a shorter one it starts with ("SPECIAL ORDER ITEM").
    const tags = [
        ...cfg.lines.map((t) => ({ id: t.id as string | null, texts: wordings(t, t.text), values: t.values })),
        ...cfg.retired.lines.map((text) => ({ id: null, texts: [text], values: [] as string[] })),
    ].toSorted((a, b) => Math.max(...b.texts.map((t) => t.length)) - Math.max(...a.texts.map((t) => t.length)));
    for (const tag of tags) {
        for (let i = lines.length - 1; i > 0; i--) {
            const value = lineValue(lines[i] ?? "", tag.texts);
            if (value === null) continue;
            if (tag.id) found[tag.id] = tag.values.find((v) => same(v, value)) ?? value.toLowerCase();
            lines.splice(i, 1);
            break;
        }
    }
    while (lines.length > 1 && !lines.at(-1)?.trim()) lines.pop();
    const first = lines[0] ?? "";
    const ending = bracketsAtEnd(first);
    const parsed = ending ? parseTags(ending.inside, cfg) : null;
    if (parsed && ending) lines[0] = first.slice(0, ending.start).trimEnd();
    const lineBreak = full.includes("\r\n") ? "\r\n" : "\n";
    return { base: lines.join(lineBreak), choices: parsed?.choices ?? {}, includes: parsed?.includes ?? [], excludes: parsed?.excludes ?? [], lines: found };
}

/** The ServiceM8 name for a typed name and its tags. */
export function composeName(parts: NameParts, cfg: NameTagConfig): string {
    // ServiceM8 names use \r\n between lines; keep a typed name's own (\n) line breaks if it has some.
    const lineBreak = parts.base.replaceAll("\r\n", "").includes("\n") ? "\n" : "\r\n";
    const lines = parts.base.trimEnd().split(/\r?\n/);
    const tags = tagText(parts, cfg);
    if (tags) lines[0] = `${(lines[0] ?? "").trimEnd()} (${tags})`;
    for (const t of cfg.lines) {
        const value = parts.lines[t.id];
        if (value !== undefined) lines.push(lineText(t, value));
    }
    return lines.join(lineBreak);
}

/** `name` with one change applied. */
export const applyNameChange = (name: string, change: NameChange, cfg: NameTagConfig) => composeName(changeParts(splitName(name, cfg), change), cfg);

/** A name's parts with one change applied (in place). */
function changeParts(parts: NameParts, change: NameChange): NameParts {
    if (change.base !== undefined) parts.base = change.base;
    if (change.choice) {
        if (change.choice.option === null) delete parts.choices[change.choice.id];
        else parts.choices[change.choice.id] = change.choice.option;
    }
    if (change.include) {
        const { id, state } = change.include;
        for (const kind of LIST_KINDS) parts[kind] = parts[kind].filter((i) => i !== id);
        if (state) parts[state].push(id);
    }
    if (change.line) {
        if (change.line.value === null) delete parts.lines[change.line.id];
        else parts.lines[change.line.id] = change.line.value;
    }
    return parts;
}

/** The name with its tags in the current wording (old wording and retired tags tidied away). */
export const tidyName = (name: string, cfg: NameTagConfig) => composeName(splitName(name, cfg), cfg);

/** The tags without their history: only what's written now. */
function currentOnly(cfg: NameTagConfig): NameTagConfig {
    return {
        choices: cfg.choices.map((c) => ({ ...c, options: c.options.map((o) => ({ id: o.id, text: o.text })) })),
        includes: cfg.includes.map((i) => ({ id: i.id, name: i.name })),
        lines: cfg.lines.map((l) => ({ id: l.id, label: l.label, text: l.text, values: l.values })),
        retired: { segments: [], includes: [], lines: [] },
    };
}

/**
 * The name to update to, when a name reads its tags only through old wording or a deleted tag;
 * else null. Names in another accepted style ("Installation and Keys", other case) are left alone.
 */
export function nameUpdate(name: string, cfg: NameTagConfig): string | null {
    const parts = splitName(name, cfg);
    if (JSON.stringify(parts) === JSON.stringify(splitName(name, currentOnly(cfg)))) return null;
    return composeName(parts, cfg);
}

/**
 * Name tags set for an item (by its tags: smartTags.ts), by tag id: a choice's option (null: none), an include's list (null:
 * not mentioned), a line's value ("" = no value; null: no line). Only those listed are set: the rest are up to the item's name.
 */
export interface SetTags {
    choices: Record<string, string | null>;
    includes: Record<string, ListKind | null>;
    lines: Record<string, string | null>;
}

/** The tags set, as settings have them now: tags deleted in settings (or a choice's option deleted) drop out. */
export function liveSetTags(set: Partial<SetTags>, cfg: NameTagConfig): SetTags {
    const keep = <T>(r: Record<string, T> | undefined, ok: (id: string, v: T) => boolean) => Object.fromEntries(Object.entries(r ?? {}).filter(([id, v]) => ok(id, v)));
    return {
        choices: keep(set.choices, (id, option) => cfg.choices.some((c) => c.id === id && (option === null || c.options.some((o) => o.id === option)))),
        includes: keep(set.includes, (id) => cfg.includes.some((i) => i.id === id)),
        lines: keep(set.lines, (id) => cfg.lines.some((l) => l.id === id)),
    };
}

/** A kind of name tag, as a key says it. */
export type TagKind = "choice" | "include" | "line";

/** A tag, by kind and id: "include:keys" (which an item overrides, say). */
export const tagKey = (kind: TagKind, id: string) => `${kind}:${id}`;

/** Every tag set, by key (tagKey), with its value. */
export const setEntries = (set: Partial<SetTags>): [string, string | null][] => [
    ...Object.entries(set.choices ?? {}).map(([id, v]): [string, string | null] => [tagKey("choice", id), v]),
    ...Object.entries(set.includes ?? {}).map(([id, v]): [string, string | null] => [tagKey("include", id), v]),
    ...Object.entries(set.lines ?? {}).map(([id, v]): [string, string | null] => [tagKey("line", id), v]),
];

/** Some tags set: only those given, each with its value (as SetTags has them), e.g. what one tag's wording says. */
export type OwnTags = Partial<SetTags>;

/** Tags (all, or some), each as a change to a name, with its key (tagKey). */
function changesOf(set: OwnTags): [string, NameChange][] {
    return [
        ...Object.entries(set.choices ?? {}).map(([id, option]): [string, NameChange] => [tagKey("choice", id), { choice: { id, option } }]),
        ...Object.entries(set.includes ?? {}).map(([id, state]): [string, NameChange] => [tagKey("include", id), { include: { id, state } }]),
        ...Object.entries(set.lines ?? {}).map(([id, value]): [string, NameChange] => [tagKey("line", id), { line: { id, value } }]),
    ];
}

/** A name with some tags set (only those given; the rest of it as it is). */
export function applyTags(name: string, set: OwnTags, cfg: NameTagConfig): string {
    const changes = changesOf(set);
    if (!changes.length) return name;
    const parts = splitName(name, cfg);
    const next = changes.reduce((p, [, change]) => changeParts(p, change), structuredClone(parts));
    return tagsKey(next) === tagsKey(parts) ? name : composeName(next, cfg);
}

/**
 * A name with these tags (those deleted in settings dropped), if any of them read differently in it now; else null. Only tags are
 * compared, so the rest of the name (and its wording style) is left alone.
 */
export function setTagsName(name: string, set: SetTags, cfg: NameTagConfig): string | null {
    const next = applyTags(name, liveSetTags(set, cfg), cfg);
    return next === name ? null : next;
}

/** A name's tags, comparable: order and case aside. */
const tagsKey = (p: NameParts) =>
    [
        ...Object.entries(p.choices).map(([id, option]) => `choice:${id}=${option}`),
        ...p.includes.map((id) => `includes:${id}`),
        ...p.excludes.map((id) => `excludes:${id}`),
        ...Object.entries(p.lines).map(([id, value]) => `line:${id}=${value.toLowerCase()}`),
    ]
        .toSorted((a, b) => a.localeCompare(b))
        .join("\n");

/** The typed name's first line, for lists. */
export const displayName = (full: string, cfg: NameTagConfig) => splitName(full, cfg).base.split(/\r?\n/)[0] ?? "";

const slug = (s: string) =>
    s
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, "-")
        .replaceAll(/^-|-$/g, "") || newId();
const titleCase = (s: string) => s.replaceAll(/\b[a-z]/g, (c) => c.toUpperCase());

/** A name's bracket and the words of its "Includes ..." and "Excludes ..." lists, if it has any. */
function tagLists(name: string) {
    const ending = bracketsAtEnd(name.split(/\r?\n/)[0] ?? "");
    const at = ending?.inside.search(/\b(?:includes?|excludes?)\s/i) ?? -1;
    if (!ending || at < 0) return null;
    const words = ending.inside
        .slice(at)
        .split(NEXT_LIST)
        .flatMap((part) => listWords(listItem(part)?.item ?? ""));
    return words.length ? { inside: ending.inside, words } : null;
}

/**
 * Includes already in a business's names that aren't tags yet ("(Includes Keys)" or
 * "(Excludes Keys)" -> Keys), so they read as tags from the start. Only words in at least two
 * names (one-off lists, like the towns a call-out covers, stay part of the name), from brackets
 * that are all tags once they're added.
 */
export function detectIncludes(names: string[], cfg: NameTagConfig): IncludeTag[] {
    const lists = names.flatMap((n) => tagLists(n) ?? []);
    const counts = new Map<string, number>();
    for (const l of lists) for (const w of new Set(l.words.map((x) => x.toLowerCase()))) counts.set(w, (counts.get(w) ?? 0) + 1);
    const found: IncludeTag[] = [...cfg.includes];
    for (const l of lists) {
        const extra = unknownIncludes(
            l.words.filter((w) => (counts.get(w.toLowerCase()) ?? 0) >= 2),
            found,
            cfg,
        );
        if (extra.length && parseTags(l.inside, { ...cfg, includes: [...found, ...extra] })) found.push(...extra);
    }
    return found;
}

/** Words of the lists that aren't tags yet (not an include, nor an "Includes ..." or "Excludes ..." option), once each. */
function unknownIncludes(words: string[], known: IncludeTag[], cfg: NameTagConfig): IncludeTag[] {
    const isOption = (w: string) => cfg.choices.some((c) => c.options.some((o) => same(listItem(o.text)?.item ?? "\0", w)));
    const extra: IncludeTag[] = [];
    for (const w of words) {
        if (w.length > 40 || isOption(w) || [...known, ...extra].some((i) => same(i.name, w))) continue;
        extra.push({ id: slug(w), name: titleCase(w) });
    }
    return extra;
}

/**
 * Checks wordings are unique (case aside) and free of brackets and line breaks, which names are
 * split on. `keys`: where the wording is read (default: as a segment of its own).
 */
function wordingChecker() {
    const seen = new Set<string>();
    return (text: string, what: string, keys: string[] = [text]) => {
        if (!text.trim()) return `${what} needs some wording`;
        if (/[()\r\n]/.test(text)) return `${what} can't contain brackets or line breaks`;
        const lower = keys.map((k) => k.trim().toLowerCase());
        if (lower.some((k) => seen.has(k))) return `"${text.trim()}" is used twice`;
        for (const k of lower) seen.add(k);
        return null;
    };
}

/** Where a list word is read: in its list (an option's), or in both (an include's, set either way per item). */
const listKeys = (item: string, kinds: readonly ListKind[]) => kinds.map((k) => `${k}:${item}`);

/** An include's wording can't hold what separates includes ("A, B & C", "A and B"). */
const listsSeveral = (text: string) => /[,&]/.test(text) || / and /i.test(` ${text.trim()} `.replaceAll(/\s+/g, " "));

function choiceProblem(c: ChoiceTag, unique: ReturnType<typeof wordingChecker>) {
    if (!c.label.trim()) return "Every choice needs a name";
    if (!c.options.length) return `"${c.label}" needs at least one option`;
    for (const o of c.options) {
        const listed = listItem(o.text);
        const what = `An option of "${c.label}"`;
        const problem = listed ? unique(listed.item, what, listKeys(listed.item, [listed.kind])) : unique(o.text, what);
        if (problem) return problem;
        if (o.text.includes(",")) return `"${o.text}" can't contain a comma`;
        if (listed && listsSeveral(listed.item)) return `"${o.text}" can't list several things (add an include for each)`;
    }
    return null;
}

function lineProblem(l: LineTag, unique: ReturnType<typeof wordingChecker>) {
    if (!l.label.trim()) return "Every line needs a name";
    // A line can hold brackets ("SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)"): it's matched as a whole line.
    const problem = unique(l.text.replaceAll(/[()]/g, ""), `"${l.label}"'s line`, [l.text]);
    if (problem) return problem;
    if (l.values.some((v) => /[()\r\n]/.test(v) || !v.trim())) return `"${l.label}"'s values can't be blank or contain brackets`;
    return null;
}

/** Why a set of tags can't be used, or null. Wordings must be unique and can't hold the characters names are split on. */
export function nameTagProblem(cfg: NameTagConfig): string | null {
    const unique = wordingChecker();
    for (const c of cfg.choices) {
        const problem = choiceProblem(c, unique);
        if (problem) return problem;
    }
    for (const i of cfg.includes) {
        const problem = unique(i.name, "An include", [i.name, ...listKeys(i.name, LIST_KINDS)]);
        if (problem) return problem;
        if (listsSeveral(i.name)) return `"${i.name}" can't contain commas, "&" or "and" (they separate includes)`;
    }
    for (const l of cfg.lines) {
        const problem = lineProblem(l, unique);
        if (problem) return problem;
    }
    return null;
}

/** A wording as read out of a name: an "Includes X" or "Excludes X" option is read as X in a list. */
function retireWordings(retired: NameTagConfig["retired"], texts: string[]) {
    for (const t of texts) {
        const listed = listItem(t);
        if (listed) retired.includes.push(listed.item);
        else retired.segments.push(t);
    }
}

/**
 * The tags after an edit, keeping old wording readable: a reworded tag keeps its earlier
 * wording as an alias, and a deleted one's wording is retired (both are still read out of names).
 */
export function withHistory(before: NameTagConfig, edited: NameTagConfig): NameTagConfig {
    const aliasesFor = (old: { aliases?: string[] } | undefined, oldText: string | undefined, now: { aliases?: string[] }, text: string) =>
        old ? [...new Set([...(old.aliases ?? []), ...(now.aliases ?? []), oldText ?? ""])].filter((t) => t.trim() && !same(t, text)) : now.aliases;
    const retired = { segments: [...edited.retired.segments], includes: [...edited.retired.includes], lines: [...edited.retired.lines] };

    const choices = edited.choices.map((c) => {
        const was = before.choices.find((x) => x.id === c.id);
        for (const o of was?.options ?? []) if (!c.options.some((x) => x.id === o.id)) retireWordings(retired, wordings(o, o.text));
        return {
            ...c,
            options: c.options.map((o) => {
                const old = was?.options.find((x) => x.id === o.id);
                return { ...o, aliases: aliasesFor(old, old?.text, o, o.text) };
            }),
        };
    });
    for (const c of before.choices) if (!edited.choices.some((x) => x.id === c.id)) for (const o of c.options) retireWordings(retired, wordings(o, o.text));

    const includes = edited.includes.map((i) => {
        const old = before.includes.find((x) => x.id === i.id);
        return { ...i, aliases: aliasesFor(old, old?.name, i, i.name) };
    });
    for (const i of before.includes) if (!edited.includes.some((x) => x.id === i.id)) retired.includes.push(...wordings(i, i.name));

    const lines = edited.lines.map((l) => {
        const old = before.lines.find((x) => x.id === l.id);
        return { ...l, aliases: aliasesFor(old, old?.text, l, l.text) };
    });
    for (const l of before.lines) if (!edited.lines.some((x) => x.id === l.id)) retired.lines.push(...wordings(l, l.text));

    // A wording that's in use again isn't retired.
    const live = { choices, includes, lines };
    const inUse = (t: string) =>
        live.choices.some((c) => c.options.some((o) => wordings(o, o.text).some((w) => same(w, t) || same(listItem(w)?.item ?? "\0", t)))) ||
        live.includes.some((i) => wordings(i, i.name).some((w) => same(w, t))) ||
        live.lines.some((l) => wordings(l, l.text).some((w) => same(w, t)));
    const tidy = (list: string[]) => [...new Set(list.map((t) => t.trim()))].filter((t) => t && !inUse(t));
    return { ...live, retired: { segments: tidy(retired.segments), includes: tidy(retired.includes), lines: tidy(retired.lines) } };
}
